import React, { useCallback, useMemo, useState } from 'react';
import { useWatch, useFormContext, useFieldArray } from 'react-hook-form';
import { OwnerType, TOwnerLink, TOwnershipStructure } from '../../types.ts';
import {
  buildTree,
  findNodeByLinkId,
  getEmptyLink,
  getEmptyOwner,
  getSiblingLinks,
  setAllFieldsTouched,
} from '../../utils/utils.tsx';

import { AddOwnerButton } from '../AddOwnerButton.tsx';
import { OwnerCard } from './OwnerCard';
import { EmptyOwnersList } from './EmptyOwnersList.tsx';
import { ROOT_OWNER_NODE } from '../../constants.ts';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { DragDropManager } from '@dnd-kit/dom';
import { OwnerCardView } from './OwnerCardView.tsx';
import { applyDragPosition, syncClones } from '../../utils/collisionUtils.ts';
import { DragEndEvent, DragMoveEvent, DragOverEvent } from '@dnd-kit/abstract';
import { Box, SxProps } from '@mui/material';
import { styles } from '../../styles.ts';

interface IOwnerCardListProps {
  handleOpen: (index: number) => void;
  handleDeleteOpen: (index: number) => void;
  setDeleteFunc: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  handleCalculateOwnershipPercentage: () => void;
}

export const OwnerCardList = ({
  handleOpen,
  handleDeleteOpen,
  setDeleteFunc,
  handleCalculateOwnershipPercentage,
}: IOwnerCardListProps) => {
  const { control, getValues, trigger, setValue } = useFormContext<TOwnershipStructure>();

  const [owners, link] = useWatch({ control, name: ['owners', 'link'] });
  const { append: addOwner } = useFieldArray({ control, name: 'owners' });

  const [activeLinkId, setActiveLinkId] = useState<string | null>(null);
  const [dropParent, setDropParent] = useState<{ linkId: string | null; allowed: boolean } | null>(
    null
  );

  const tree = useMemo(() => buildTree(owners, link), [owners, link]);

  const handleSetDropParent = (linkId: string | null, allowed: boolean) =>
    setDropParent({ linkId, allowed });

  const handleDragOver = (event: DragOverEvent, manager: DragDropManager) => {
    event.preventDefault();
    const source = event.operation?.source ?? manager?.dragOperation?.source;

    if (!source) return;
    setActiveLinkId(source.id as string);
    applyDragPosition(
      event,
      manager,
      { getValues, setValue, trigger },
      handleSetDropParent,
      handleCalculateOwnershipPercentage
    );
  };

  const onDragMove = (event: DragMoveEvent, manager: DragDropManager) => {
    applyDragPosition(
      event,
      manager,
      { getValues, setValue, trigger },
      handleSetDropParent,
      handleCalculateOwnershipPercentage
    );
  };

  const onDragEnd = (event: DragEndEvent) => {
    const sourceId = event?.operation?.source?.id as string;
    const links = getValues('link');
    const owners = getValues('owners');
    const sourceLinkIndex = links.findIndex((item) => item.id === sourceId);

    const draggedOwnerIndex = owners.findIndex(
      (owner) => owner.id === links[sourceLinkIndex]?.fromNodeId
    );

    setAllFieldsTouched(
      `owners.${draggedOwnerIndex}`,
      getValues(`owners.${draggedOwnerIndex}`),
      setValue
    );
    trigger('owners');
    trigger('link');

    setActiveLinkId(null);
    setDropParent(null);
  };

  const handleAddItem = useCallback(
    (type: OwnerType, parentId?: string, parentLinkId?: string | null) => {
      const owner = getEmptyOwner(type);
      const links = getValues('link');
      const owners = getValues('owners');
      const parentIndex = owners.findIndex((o) => o.id === parentId);
      const displayOrder = getSiblingLinks(links, parentLinkId ?? null).length;
      const isWithoutFormalOwnership = type === OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP;
      const newLink: TOwnerLink = {
        ...getEmptyLink(
          owner.id,
          parentId || ROOT_OWNER_NODE,
          null,
          isWithoutFormalOwnership ? 0 : null
        ),
        parentLinkId: parentLinkId ?? null,
        displayOrder,
      };

      addOwner(owner);
      let nextLinks: TOwnerLink[] = [...links, newLink];
      nextLinks = syncClones(nextLinks, newLink, null, owners);
      setValue('link', nextLinks, { shouldDirty: true });

      const primaryIndex = nextLinks.findIndex((l) => l.id === newLink.id);
      handleOpen(primaryIndex);

      trigger('isFoundersAndBeneficiariesAbsent');
      trigger(`owners.${parentIndex}`);
    },
    [getValues, setValue, handleOpen, addOwner, trigger]
  );

  return (
    <DragDropProvider onDragOver={handleDragOver} onDragMove={onDragMove} onDragEnd={onDragEnd}>
      <Box
        sx={
          styles.dropZoneContainer({
            isRoot: true,
            isOver: !!activeLinkId && dropParent === null,
          }) as SxProps
        }
      >
        {tree.length ? (
          tree.map((item, index) => (
            <OwnerCard
              key={item.linkId}
              owner={item}
              index={index}
              handleOpen={handleOpen}
              onDelete={handleDeleteOpen}
              handleAddItem={handleAddItem}
              activeLinkId={activeLinkId}
              setDeleteFunc={setDeleteFunc}
              dropParent={dropParent}
            />
          ))
        ) : (
          <EmptyOwnersList />
        )}
      </Box>
      <AddOwnerButton
        handleAddOwner={handleAddItem}
        parentId={ROOT_OWNER_NODE}
        parentLinkId={null}
        level={0}
      />
      <DragOverlay>
        {(source) => <OwnerCardView owner={findNodeByLinkId(source.id as string, tree)} />}
      </DragOverlay>
    </DragDropProvider>
  );
};
